import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { SectionHeading } from "./Reveal";
import { BotanicalDivider } from "./Botanical";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHAPE_CLS = {
    arch: "rounded-t-[10rem] rounded-b-2xl aspect-[3/4]",
    circle: "rounded-full aspect-square",
    soft: "rounded-2xl aspect-[4/5]",
};

export const Gallery = () => {
    const [items, setItems] = useState([]);
    const [active, setActive] = useState(null);
    const [touchX, setTouchX] = useState(null);

    useEffect(() => {
        axios.get(`${API}/gallery`).then(({ data }) => setItems(data)).catch(() => {});
    }, []);

    const close = useCallback(() => setActive(null), []);
    const step = useCallback(
        (dir) => setActive((a) => (a === null ? null : (a + dir + items.length) % items.length)),
        [items.length]
    );

    useEffect(() => {
        if (active === null) return;
        const onKey = (e) => {
            if (e.key === "Escape") close();
            if (e.key === "ArrowRight") step(1);
            if (e.key === "ArrowLeft") step(-1);
        };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [active, close, step]);

    return (
        <section id="gallery" data-testid="gallery-section" className="relative bg-cream py-28 lg:py-36">
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <SectionHeading
                    eyebrow="Portfolio"
                    title="Moments We've Styled"
                    script="a glimpse of the glow"
                />
                <div className="mt-6 flex justify-center">
                    <BotanicalDivider />
                </div>

                <div className="mt-16 columns-2 gap-6 md:columns-3 [column-fill:_balance]">
                    {items.map((img, i) => (
                        <motion.button
                            key={img.id}
                            data-testid={`gallery-item-${i}`}
                            onClick={() => setActive(i)}
                            initial={{ opacity: 0, y: 34 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.7, delay: (i % 3) * 0.12, ease: [0.22, 1, 0.36, 1] }}
                            className="group relative mb-6 block w-full break-inside-avoid"
                            aria-label={`Open ${img.caption}`}
                        >
                            <div className={`overflow-hidden border border-gold/50 p-1.5 ${SHAPE_CLS[img.shape]}`}>
                                <div className="h-full w-full overflow-hidden" style={{ borderRadius: "inherit" }}>
                                    <img
                                        src={img.url}
                                        alt={img.caption}
                                        loading="lazy"
                                        className="h-full w-full object-cover transition-transform duration-[1200ms] ease-elegant group-hover:scale-108"
                                        style={{ transform: "scale(1.001)" }}
                                    />
                                </div>
                            </div>
                            <span className="pointer-events-none absolute inset-x-0 bottom-4 text-center font-script text-2xl text-ivory opacity-0 drop-shadow-[0_2px_8px_rgba(10,43,33,0.8)] transition-opacity duration-500 group-hover:opacity-100">
                                {img.caption}
                            </span>
                        </motion.button>
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {active !== null && items[active] && (
                    <motion.div
                        data-testid="gallery-lightbox"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="fixed inset-0 z-[70] flex items-center justify-center bg-emerald-deep/95 p-6 backdrop-blur-md"
                        onClick={close}
                        onTouchStart={(e) => setTouchX(e.touches[0].clientX)}
                        onTouchEnd={(e) => {
                            if (touchX === null) return;
                            const dx = e.changedTouches[0].clientX - touchX;
                            if (Math.abs(dx) > 50) step(dx < 0 ? 1 : -1);
                            setTouchX(null);
                        }}
                    >
                        <button
                            data-testid="lightbox-close-btn"
                            onClick={close}
                            className="absolute right-6 top-6 text-gold transition-transform duration-300 hover:scale-110"
                            aria-label="Close lightbox"
                        >
                            <X size={30} />
                        </button>
                        <button
                            data-testid="lightbox-prev-btn"
                            onClick={(e) => { e.stopPropagation(); step(-1); }}
                            className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full border border-gold/40 p-3 text-gold transition-colors duration-300 hover:bg-gold hover:text-emerald-deep sm:left-8"
                            aria-label="Previous image"
                        >
                            <ChevronLeft size={22} />
                        </button>
                        <button
                            data-testid="lightbox-next-btn"
                            onClick={(e) => { e.stopPropagation(); step(1); }}
                            className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full border border-gold/40 p-3 text-gold transition-colors duration-300 hover:bg-gold hover:text-emerald-deep sm:right-8"
                            aria-label="Next image"
                        >
                            <ChevronRight size={22} />
                        </button>
                        <motion.figure
                            key={active}
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            className="max-h-[82vh] max-w-4xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={items[active].url}
                                alt={items[active].caption}
                                className="max-h-[74vh] w-full border border-gold/50 object-contain p-1.5"
                            />
                            <figcaption className="mt-4 text-center font-script text-3xl text-gold" data-testid="lightbox-caption">
                                {items[active].caption}
                            </figcaption>
                        </motion.figure>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};
